import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { type Sessione, useScegliAzienda, useUscita } from '../api/sessione.js';
import { useAvvisi } from '../api/avvisi.js';
import { etichettaLivello } from '../etichette.js';
import { usePuoAprire } from '../permessi.js';
import { t } from '../i18n/lingua.js';
import { MenuAspetto } from './MenuAspetto.js';
import { RicercaRapida } from './RicercaRapida.js';

/**
 * Barra in alto: azienda in uso, ricerca rapida, aspetto, profilo.
 *
 * Il cambio azienda è qui e non nascosto nelle impostazioni perché è
 * un'operazione di tutti i giorni: chi segue sia La Regina S.p.A. sia Inc
 * passa dall'una all'altra più volte al giorno.
 */
export function BarraSuperiore({
  sessione,
  menuAperto,
  onMenu,
}: {
  sessione: Sessione;
  /** Sul telefono il menu è un cassetto, e questo è il suo interruttore. */
  menuAperto?: boolean;
  onMenu?: () => void;
}) {
  const [apertoAziende, setApertoAziende] = useState(false);
  const [apertoProfilo, setApertoProfilo] = useState(false);
  const angolo = useRef<HTMLDivElement>(null);
  const contenitore = useRef<HTMLDivElement>(null);
  const scegli = useScegliAzienda();
  const uscita = useUscita();
  const puo = usePuoAprire();
  const avvisi = useAvvisi(puo('sales-orders'));

  const inUso =
    sessione.user.companies.find((a: { id: number }) => a.id === sessione.companyId) ?? sessione.user.companies[0];
  const piuAziende = sessione.user.companies.length > 1;

  useEffect(() => {
    if (!apertoAziende) return;
    const chiudi = (e: MouseEvent) => {
      if (!contenitore.current?.contains(e.target as Node)) setApertoAziende(false);
    };
    // Esc chiude, come ogni altra cosa che si apre sopra la pagina
    const conEsc = (e: KeyboardEvent) => e.key === 'Escape' && setApertoAziende(false);
    document.addEventListener('mousedown', chiudi);
    document.addEventListener('keydown', conEsc);
    return () => {
      document.removeEventListener('mousedown', chiudi);
      document.removeEventListener('keydown', conEsc);
    };
  }, [apertoAziende]);

  useEffect(() => {
    if (!apertoProfilo) return;
    const chiudi = (e: MouseEvent) => {
      if (!angolo.current?.contains(e.target as Node)) setApertoProfilo(false);
    };
    const conEsc = (e: KeyboardEvent) => e.key === 'Escape' && setApertoProfilo(false);
    document.addEventListener('mousedown', chiudi);
    document.addEventListener('keydown', conEsc);
    return () => {
      document.removeEventListener('mousedown', chiudi);
      document.removeEventListener('keydown', conEsc);
    };
  }, [apertoProfilo]);

  const iniziali = sessione.user.name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <header className="topbar">
      {/* solo sul telefono: sullo schermo grande il menu è già lì di fianco */}
      {onMenu && (
        <button
          className="icon-btn solo-stretto"
          onClick={onMenu}
          aria-label={menuAperto ? t('Chiudi il menu') : t('Apri il menu')}
          aria-expanded={menuAperto}
          aria-controls="menu-principale"
        >
          {menuAperto ? '✕' : '≡'}
        </button>
      )}

      <div className="menu-wrap" ref={contenitore}>
        <button
          className="company-switch"
          onClick={() => piuAziende && setApertoAziende((v) => !v)}
          aria-haspopup={piuAziende ? 'menu' : undefined}
          aria-expanded={piuAziende ? apertoAziende : undefined}
          disabled={!piuAziende}
        >
          <span className="dot" style={{ color: 'var(--accent)' }} />
          <strong>{inUso?.code ?? '—'}</strong>
          <span className="dim solo-largo">{inUso?.name ?? t('Nessuna azienda')}</span>
          {piuAziende && <span className="dim">▾</span>}
        </button>
        {apertoAziende && (
          <div className="menu" role="menu" style={{ left: 0, right: 'auto' }}>
            <div className="menu-label">{t('Lavora in')}</div>
            {sessione.user.companies.map((azienda) => (
              <button
                key={azienda.id}
                className="menu-item"
                role="menuitemradio"
                aria-checked={azienda.id === sessione.companyId}
                onClick={() => {
                  setApertoAziende(false);
                  scegli.mutate(azienda.id);
                }}
              >
                {/* il codice non è un simbolo: «glyph» è largo un carattere, e
                    «SPA» ci finiva sopra il nome */}
                <span className="menu-codice">{azienda.code}</span>
                <span>{azienda.name}</span>
                <span className="tick">✓</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* la ricerca cerca ordini, clienti e articoli: a chi non ne vede
          nessuno — la produzione — non troverebbe mai niente */}
      {(puo('sales-orders') || puo('customers') || puo('items')) && <RicercaRapida />}

      <div className="topbar-right">
        <MenuAspetto aspetto={sessione.user.theme} />
        {/* la campanella porta agli avvisi e dice quanti sono: un numero
            addosso all'icona si vede senza andarlo a cercare */}
        {puo('sales-orders') && (
          <Link className="icon-btn" to="/alerts" title={t('Avvisi')}>
            ◔
            {(avvisi.data?.total ?? 0) > 0 && (
              <span className="pallino-avvisi">{avvisi.data!.total}</span>
            )}
          </Link>
        )}
        {/* l'avatar dice chi sei e porta al profilo, che è dove stanno la
            password e le preferenze: una cosa sola, in un posto solo */}
        <div ref={angolo} style={{ position: 'relative' }}>
          <button
            className="avatar"
            title={`${sessione.user.name} · ${etichettaLivello(sessione.user.accessLevel)}`}
            aria-haspopup="menu"
            aria-expanded={apertoProfilo}
            onClick={() => setApertoProfilo((v) => !v)}
          >
            {iniziali}
          </button>
          {apertoProfilo && (
            <div className="menu" role="menu">
              <div className="menu-label">{sessione.user.email}</div>
              <Link
                className="menu-item"
                role="menuitem"
                to="/profile"
                onClick={() => setApertoProfilo(false)}
              >
                {t('Profilo e password')}
              </Link>
            </div>
          )}
        </div>
        <button
          className="btn btn-ghost solo-largo"
          onClick={() => uscita.mutate()}
          disabled={uscita.isPending}
        >
          {uscita.isPending ? t('Esco…') : t('Esci')}
        </button>
      </div>
    </header>
  );
}
